import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal, { EASE } from "@/components/Reveal";
import { Plus } from "lucide-react";

const faqs = [
  {
    q: "What does CtrlB specialise in?",
    a: "LinkedIn and personal brand growth. Positioning, content, creative and consistent audience growth for founders, executives and businesses.",
  },
  {
    q: "Is CtrlB a LinkedIn management agency?",
    a: "No. CtrlB is an independent, founder-led consultancy built around white-glove strategy. You work directly with me on positioning, content and direction — not through account layers.",
  },
  {
    q: "How many clients do you work with at a time?",
    a: "A small number, deliberately. White-glove strategy doesn't scale to a roster of fifty — every client gets my direct attention, so I take on new engagements only when I can give them that.",
  },
  {
    q: "Do you create the content as well as the strategy?",
    a: "Yes. Strategy is only useful when something gets built. Content and creative are part of the work, not an add-on.",
  },
  {
    q: "Can you help with personal branding?",
    a: "That's the centre of the work: defining what you should be known for, then building the presence around it.",
  },
  {
    q: "Do you provide graphic design and video?",
    a: "Yes. Design and video are part of LinkedIn creative. When the work needs specialist execution, I bring in trusted creatives from my network.",
  },
  {
    q: "Can you help with LinkedIn lead generation?",
    a: "Yes. The system is built to turn attention into audience, relationships and business opportunities — not just impressions.",
  },
  {
    q: "Do you work directly with clients?",
    a: "Always. Strategy, positioning and direction stay with me from the first conversation onwards.",
  },
  {
    q: "How does an engagement start?",
    a: "With a discover call. We talk about what you want to be known for, where your presence stands, and whether we're the right fit for each other. If we are, I'll propose the way forward.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="bg-[#09090B] border-t border-[#F3F0E8]/10 scroll-mt-20">
      <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <Reveal>
          <h2
            data-testid="faq-heading"
            className="font-serif-d font-black tracking-tight leading-[1.0] text-4xl sm:text-6xl text-[#F3F0E8]"
          >
            Questions, <span className="italic font-semibold text-[#F3F0E8]/45">answered.</span>
          </h2>
        </Reveal>

        <div className="mt-14 border-t border-[#F3F0E8]/10">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={i * 0.04}>
                <div className="border-b border-[#F3F0E8]/10">
                  <button
                    type="button"
                    data-testid={`faq-question-${i + 1}`}
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between gap-6 py-6 sm:py-7 text-left group"
                  >
                    <span className={`font-serif-d font-semibold tracking-tight text-xl sm:text-2xl transition-colors duration-300 ${isOpen ? "text-[#CFE9EF]" : "text-[#F3F0E8]/80 group-hover:text-[#F3F0E8]"}`}>
                      {f.q}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      className="shrink-0"
                    >
                      <Plus className={`w-5 h-5 transition-colors duration-300 ${isOpen ? "text-[#118C4F]" : "text-[#F3F0E8]/40"}`} aria-hidden="true" />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <p data-testid={`faq-answer-${i + 1}`} className="text-sm sm:text-base text-[#F3F0E8]/60 leading-relaxed pb-7 max-w-2xl">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
